
import { useMemo } from "react";
import { format } from "sql-formatter";
import Prism from "prismjs";
import "prismjs/components/prism-sql";
import "prismjs/components/prism-json";
import { CodeCopyButton } from "./CodeCopyButton";
import { toSafeCodeHtml } from "./code-display-utils";

type SqlDialect = "sql" | "postgresql" | "mysql" | "bigquery" | "sqlite";

// Map resource subtypes to sql-formatter dialects
const dialectMap: Record<string, SqlDialect> = {
  postgresql: "postgresql",
  postgres: "postgresql",
  mysql: "mysql",
  bigquery: "bigquery",
  sqlite: "sqlite",
  d1: "sqlite", // Cloudflare D1 uses SQLite
};

function getDialect(input?: string): SqlDialect {
  if (!input) return "sql";
  return dialectMap[input.toLowerCase()] || "sql";
}

interface SqlDisplayProps {
  sql: string;
  dialect?: string; // Accept any string, will be mapped
  maxHeight?: string;
  showCopyButton?: boolean;
  className?: string;
}

export function SqlDisplay({
  sql,
  dialect = "sql",
  maxHeight = "300px",
  showCopyButton = true,
  className = "",
}: SqlDisplayProps) {
  // Format SQL - memoized to avoid re-formatting on every render
  const formattedSql = useMemo(() => {
    if (!sql?.trim()) return "";
    const mappedDialect = getDialect(dialect);
    try {
      const result = format(sql, {
        language: mappedDialect,
        tabWidth: 2,
        keywordCase: "upper",
        linesBetweenQueries: 2,
      });
      return result;
    } catch {
      // If formatting fails (e.g. dialect mismatch like PostgreSQL ::text), return raw SQL
      return sql;
    }
  }, [sql, dialect]);

  // Apply Prism highlighting - generate HTML directly
  const highlightedHtml = useMemo(() => {
    if (!formattedSql) return "";
    try {
      let html = formattedSql;
      if (Prism?.languages?.sql) {
        html = Prism.highlight(formattedSql, Prism.languages.sql, "sql");
      } else {
        return toSafeCodeHtml(formattedSql);
      }
      // Convert newlines to <br> for proper rendering
      return html.replace(/\n/g, "<br/>");
    } catch {
      return toSafeCodeHtml(formattedSql);
    }
  }, [formattedSql]);

  if (!sql?.trim()) {
    return (
      <div className="text-sm text-gray-400 italic p-3">Không có SQL</div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Copy button */}
      {showCopyButton && <CodeCopyButton value={formattedSql} />}

      {/* SQL code block */}
      <div
        className="overflow-auto rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
        style={{ maxHeight }}
      >
        <pre className="p-3! sm:p-4! pr-10! sm:pr-16! m-0! text-xs sm:text-sm leading-relaxed font-mono whitespace-pre-wrap! wrap-break-word">
          <code
            className="language-sql block whitespace-pre-wrap!"
            dangerouslySetInnerHTML={{ __html: highlightedHtml }}
          />
        </pre>
      </div>
    </div>
  );
}
